import {
  ALUMINIUM_COLOR,
  BIG_SHELF_MINIMUM_HEIGHT,
  BLACK_COLOR,
  CrossPieceType,
  DoorHandleType,
  DoorHole,
  DrawerHandleType,
  ECO_COLOR,
  ECO_PLUS_COLOR,
  GLASS_COLOR,
  Material,
  MAX_WIDTH_ACCEPT_FRONT_EP,
  MIN_WIDTH_NEED_REINFORCE,
  MINIMUM_HEIGHT_SPACE_AFTER_ADDING_INTERMEDIATE_SHELF,
  MINIMUM_LARGE_DOORS_HEIGHT,
  MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES,
  NATURAL_COLOR,
  PaneMaterial,
  Position,
  PositionAngle,
  PositionLargeDoor,
  ShelfComposition,
  STANDARD_COLOR,
  WHITE_COLOR,
} from '../constants';
import { Cabinet } from './Cabinet';
import cloneDeep from 'lodash/cloneDeep';
import { Rack } from './Rack';
import { ShelfJson } from '../Data';
import { Door } from '../pieces/Door';
import { CustomPiece, KewloxPieceType } from '../pieces/CustomPiece';

import { Color } from './Color';
import { DataStorage, getColors, getPiecesData } from '../utils/dataStorage';
import { Pane } from '../pieces/Pane';
import { Drawer } from '../pieces/Drawer';
import { PositionPiece } from '../type';
import { findCombinations, getDrawersCount } from '../utils/combinationDrawers';
import { CompleteIntermediateShelf } from './CompleteIntermediateShelf';
import {
  findDoor,
  findDoorHandle,
  findDrawer,
  findDrawerHandle,
  findDrawerSlat,
} from '../utils/piecesUpdater';
import { DrawerHandle } from '../pieces/DrawerHandle';
import { DrawerSlat } from '../pieces/DrawerSlat';
import { Rod } from '../pieces/Rod';
import { findColor, getMaterialColor } from '../utils/colorHelper';
import { findISCombinations } from '../utils/intermediateShelfTools';
import { checkAndUpdate } from '../builder/change';
import { DoorHandle } from '../pieces/DoorHandle';
import {
  acceptedCrossPieceTypesForShelf,
  getDefaultCrossPieceType,
} from '../builder/crossPiecesManager';

export class Shelf {
  public level: number;
  public custom: boolean;
  public led: boolean;
  public racks: Rack[];
  public doors: CustomPiece<Door>[];
  public drawers: CustomPiece<Drawer>[];
  public intermediatesShelves: CompleteIntermediateShelf[];
  private _cabinet: Cabinet;
  public rod?: CustomPiece<Rod>;
  private _rodRemovedByUser: boolean;

  constructor(
    level: number,
    custom: boolean,
    led: boolean,
    racks: Rack[],
    doors: CustomPiece<Door>[],
    cabinet: Cabinet,
    drawers: CustomPiece<Drawer>[] = [],
    intermediatesShelves: CompleteIntermediateShelf[] = [],
    rod?: CustomPiece<Rod>,
  ) {
    this.level = level;
    this.custom = custom;
    this.led = led;
    this.racks = racks;
    this.doors = doors;
    this._cabinet = cabinet;
    this.drawers = drawers;
    this.intermediatesShelves = intermediatesShelves;
    if (rod) this.rod = rod;
    this._rodRemovedByUser = false;
  }

  static fromModel(model: ShelfJson, cabinet: Cabinet): Shelf {
    const { level, custom, led } = model;
    const pieces = getPiecesData();
    const racks = [];
    if (model.racks) {
      for (const item of model.racks) {
        racks.push(Rack.fromModel(item));
      }
      racks.sort((a, b) => b.level - a.level);
    }
    const doors = [];
    if (model.doors) {
      for (const item of model.doors) {
        doors.push(CustomPiece.fromModel<Door>(item, pieces.doors));
      }
      doors.sort(CustomPiece.compareTo);
    }

    const drawers = [];
    if (model.drawers) {
      for (const item of model.drawers) {
        drawers.push(CustomPiece.fromModel<Drawer>(item, pieces.drawers));
      }
      drawers.sort(CustomPiece.compareTo);
    }

    const intermediatesShelves = [];
    if (model.intermediatesShelves) {
      for (const item of model.intermediatesShelves) {
        intermediatesShelves.push(CompleteIntermediateShelf.fromModel(item));
      }
      intermediatesShelves.sort((a, b) => b.position - a.position);
    }

    let rod = undefined;
    if (model.rod) {
      rod = CustomPiece.fromModel<Rod>(model.rod, pieces.rods);
    }

    return new Shelf(
      level,
      custom,
      led,
      racks,
      doors,
      cabinet,
      drawers,
      intermediatesShelves,
      rod,
    );
  }

  static replacer(key: string, value: any): any {
    // Filtering out properties for serialization
    if (value[0] === '_') {
      return undefined;
    }
    return value;
  }

  isFirstShelf() {
    return this.level === 0;
  }

  isLastShelf() {
    return this.level === this.cabinet.shelves.length - 1;
  }

  getBelowShelf() {
    if (this.level > 0) return this.cabinet.shelf(this.level - 1);
  }

  getUpperRackOrTop() {
    if (this.isLastShelf()) return this.cabinet.top;
    else return this.cabinet.shelf(this.level + 1).rack(0);
  }

  addRod() {
    const searchInfo = Rod.getSearchInfos(1, this.cabinet.width);
    this.rod = getPiecesData().rods.getCustom(searchInfo, Position.Front);
    this._rodRemovedByUser = false;
    checkAndUpdate(this.cabinet);
  }

  removeRod(byUser = false) {
    if (this.rod) {
      this.rod = undefined;
      if (byUser) this._rodRemovedByUser = true;
      checkAndUpdate(this.cabinet);
    }
  }

  get height(): number {
    let height = 0;
    for (const rack of this.racks) {
      if (rack.level > 0) height += rack.realHeight;
      else height += rack.height;
    }
    return height;
  }

  set height(value: number) {
    /**
     * Update the height of the shelf
     * Transform to wardrobe if necessary
     */

    if (value >= BIG_SHELF_MINIMUM_HEIGHT) {
      // Don't add rod if we already came
      if (this.height < BIG_SHELF_MINIMUM_HEIGHT) {
        this.addRod();
      }
    } else {
      this.removeRod();
    }

    if (value >= 1190) {
      // We need 3 racks
      for (let i = this.racks.length; i < 3; i++) {
        this.rack(0).getCrossPiece(Position.Left);
        const rack = this.rack(0).clone();
        rack.level = i;
        rack.plate = undefined;
        rack.glassPlate = undefined;
        // Cross pieces will be automatically removed by checkAndUpdate()
        this.racks.push(rack);
      }
      if (value === 1390) {
        this.rack(0).height = 470;
        this.rack(1).height = 370;
        this.rack(2).height = 470;
      } else {
        for (const rack of this.racks) {
          rack.height = (value - 80) / 3;
        }
      }
    } else if (value >= 980) {
      // We need 2 racks
      if (this.racks.length > 2) {
        // Remove useless racks
        this.racks = this.racks.filter(e => e.level < 2);
      }
      for (let i = this.racks.length; i < 2; i++) {
        this.rack(0).getCrossPiece(Position.Left);
        const rack = this.rack(0).clone();
        rack.level = i;
        rack.plate = undefined;
        rack.glassPlate = undefined;
        // Cross pieces will be automatically removed by checkAndUpdate()
        this.racks.push(rack);
      }
      for (const rack of this.racks) {
        rack.height = (value - 40) / 2;
      }
    } else {
      // We need only one rack
      if (this.racks.length > 1) {
        // We remove other rack
        this.racks = this.racks.filter(rack => rack.level === 0);
      }
      // We update the height of the first rack to fill the shelf
      this.rack(0).height = value;
    }

    //TODO (question: Si custom faut il modifier la hauteur
    // de  tout les racks, certains racks ou le dernier racks ?)

    // Update doors
    if (this.doors.length > 0) {
      if (value >= MINIMUM_LARGE_DOORS_HEIGHT) {
        this.updateDoor(
          PositionAngle.FrontLeft,
          value,
          null,
          null,
          null,
          null,
          null,
          PositionLargeDoor.Inside,
        );
        this.updateDoor(
          PositionAngle.FrontRight,
          value,
          null,
          null,
          null,
          null,
          null,
          PositionLargeDoor.Outside,
        );
        this.updateDoor(
          PositionAngle.BackLeft,
          value,
          null,
          null,
          null,
          null,
          null,
          PositionLargeDoor.Outside,
        );
        this.updateDoor(
          PositionAngle.BackRight,
          value,
          null,
          null,
          null,
          null,
          null,
          PositionLargeDoor.Inside,
        );
      } else {
        for (const position of Object.values(PositionAngle)) {
          this.updateDoor(position, value, null);
        }
      }
    }

    if (this.drawers.length > 0) {
      const combo = findCombinations(
        value,
        getDrawersCount(value, this.drawers.length),
      );
      if (combo) this.setDrawersCombination(combo);
    }

    // Update Corner
    this.cabinet.updateCornerHeight();

    // Update IS
    this.autoArrangeHeightIS();

    for (const rack of this.racks) {
      rack.updateLockingTriangle();
    }
  }

  get realHeight(): number {
    /**
     * The height of the shelf which include the real height
     */
    let height = 0;
    for (const rack of this.racks) {
      height += rack.realHeight;
    }
    return height;
  }

  get cabinet(): Cabinet {
    return this._cabinet;
  }

  set cabinet(value: Cabinet) {
    this._cabinet = value;
  }

  get width(): number {
    return this._cabinet.width;
  }

  get depth(): number {
    return this._cabinet.depth;
  }

  get price(): number {
    let price = 0;
    for (const item of this.doors) {
      price += item.price;
      price += item.priceHandles;
    }
    for (const item of this.racks) price += item.price;
    for (const item of this.drawers) {
      price += item.price;
      price += item.priceHandles;
    }
    for (const item of this.intermediatesShelves) price += item.price;

    if (this.rod) price += this.rod.price;

    //TODO: !Important! ADD cost of LED to the shelf
    return price;
  }

  get weight(): number {
    let weight = 0;
    for (const item of this.doors) {
      weight += item.weight;
      weight += item.weightHandles;
    }
    for (const item of this.racks) weight += item.weight;
    for (const item of this.drawers) {
      weight += item.weight;
      weight += item.weightHandles;
    }
    for (const item of this.intermediatesShelves) weight += item.weight;

    if (this.rod) weight += this.rod.weight;
    return weight;
  }

  clone(): Shelf {
    return cloneDeep(this);
  }

  rack(level: number): Rack {
    /**
     * return the cabinet's shelf at the level of param
     */
    const rack = this.racks.find(e => e.level === level);
    if (!rack) throw new Error('No rack for this level');
    return rack;
  }

  getIntermediateShelf(
    position: number,
  ): CompleteIntermediateShelf | undefined {
    /**
     * return the cabinet's shelf at the level of param if exist
     */
    return this.intermediatesShelves.find(e => e.position === position);
  }

  getDoor(position: PositionAngle): CustomPiece<Door> | undefined {
    if (this.doors.length > 0) {
      return this.doors.find(e => e.position === position);
    }
  }

  getDrawer(position: number): CustomPiece<Drawer> | undefined {
    const drawer = this.drawers.find(e => e.position === position);
    // if (!drawer) throw new Error(`Drawer don't exist at position ${position}`);
    return drawer;
  }

  updateShelfWidth(width: number) {
    for (const position of Object.values(PositionAngle)) {
      this.updateDoor(position, null, width);
    }

    for (const drawer of this.drawers) {
      this.updateDrawer(drawer.position as number, null, width);
    }

    for (const item of this.intermediatesShelves) {
      item.updateIntermediateShelf(width);
    }

    if (this.racks) {
      this.racks.map((e: Rack) =>
        e.updateWidth(width, false, this.hasBackDoor()),
      );
    }

    if (this.rod) {
      const searchInfo = Rod.getSearchInfos(1, width);
      this.rod = getPiecesData().rods.getCustom(searchInfo, Position.Front);
    }
  }

  updateShelfElements<T extends KewloxPieceType>(
    doorsElements: CustomPiece<T>[],
    element: CustomPiece<T>,
    position: PositionPiece,
  ): CustomPiece<T>[] {
    // Remove previous entry for this position
    doorsElements = doorsElements.filter(item => item.position !== position);
    doorsElements.push(element);
    return doorsElements;
  }

  updateDoor(
    position: PositionAngle,
    height?: number | null,
    width?: number | null,
    material?: PaneMaterial | null,
    hole?: DoorHole | null,
    color?: Color | null,
    slatMaterial?: Material | null,
    largeDoorPosition?: PositionLargeDoor | null,
  ) {
    const door = this.getDoor(position);
    if (door) {
      if (
        (material &&
          (material === PaneMaterial.Glass ||
            material === PaneMaterial.Mirror)) ||
        door.base.material === PaneMaterial.Glass ||
        door.base.material === PaneMaterial.Mirror
      ) {
        hole = DoorHole.nothing;
      } else {
        if (
          material &&
          (door.material === PaneMaterial.Glass ||
            door.material === PaneMaterial.Mirror)
        ) {
          hole = DoorHole.cupLarge;
        }
      }
      const customP = findDoor(
        position,
        door,
        height,
        width,
        material,
        hole,
        color,
        slatMaterial,
        largeDoorPosition,
      );

      if (
        material &&
        (material === PaneMaterial.Glass || material === PaneMaterial.Mirror)
      ) {
        customP.handle = undefined;
      } else if (
        door.handle &&
        door.height < BIG_SHELF_MINIMUM_HEIGHT &&
        customP.base.height < BIG_SHELF_MINIMUM_HEIGHT
      ) {
        customP.handle = door.handle;
      } else if (
        height !== null &&
        height !== undefined &&
        height < MINIMUM_LARGE_DOORS_HEIGHT &&
        door.height >= MINIMUM_LARGE_DOORS_HEIGHT &&
        door.material !== PaneMaterial.Mirror
      ) {
        // Add handles
        customP.handle = DoorHandle.standardHandle();
      }

      customP.adaptHandleForDoorSlat();

      this.doors = this.updateShelfElements<Door>(
        this.doors,
        customP,
        position,
      );
    }
  }

  addDoor(position: PositionAngle) {
    if (!this.getDoor(position) && this.drawers.length === 0) {
      this.doors.push(
        Door.standardDoor(this.height, this.cabinet.width, position),
      );
      // Cross pieces will be automatically adapted by checkAndUpdate()
    }
  }

  resetBackCrossPiece() {
    this.rack(0).updateCrossPiece(
      Position.Back,
      this.cabinet.width,
      undefined,
      CrossPieceType.UpperEmptyLowerPane,
      false,
    );

    // Special case for back position, because in front,
    // ED is preferred due to depth types of plates in stock
    this.getUpperRackOrTop()?.updateCrossPiece(
      Position.Back,
      this.cabinet.width,
      undefined,
      CrossPieceType.UpperEmptyLowerPane,
      false,
    );

    // Cross Piece Manager will change to PP, ED or DD if needed,
    // goal is to not have ED which is acceptable but not wanted by default
  }

  removeDoor(position: PositionAngle) {
    if (!this.getDoor(position)) return;

    this.doors = this.doors.filter(
      (door: CustomPiece<Door>) => door.position !== position,
    );

    // Important: We must here update manually the cross pieces because
    // cross piece types DD/ED remain acceptable after removing doors but
    // we prefer no grooves by default when removing doors ; the user can add grooves after if needed
    if (
      (position === PositionAngle.FrontLeft ||
        position === PositionAngle.FrontRight) &&
      !this.hasFrontDoor()
    )
      this.rack(0).updateCrossPiece(
        Position.Front,
        this.cabinet.width,
        undefined,
        CrossPieceType.UpperEmptyLowerDoors,
      );
    else if (
      (position === PositionAngle.BackLeft ||
        position === PositionAngle.BackRight) &&
      !this.hasBackDoor()
    )
      this.resetBackCrossPiece();
  }

  removeDoors() {
    for (const position of Object.values(PositionAngle))
      this.removeDoor(position);
  }

  addDrawer(height: number, position: number) {
    if (!this.getDrawer(position)) {
      this.drawers.push(
        Drawer.standardDrawer(
          this.height,
          this.cabinet.width,
          this.cabinet.depth,
          position,
        ),
      );
    }
  }

  setDrawersComposition() {
    /**
     * Apply drawer composition to the shelf
     */

    // Ignore if we already have drawers
    if (this.drawers.length > 0) return;

    // Remove everything
    this.composition = ShelfComposition.Open;
    this.intermediatesShelves = [];

    if (this.height <= 200) {
      this.drawers.push(
        Drawer.standardDrawer(
          this.height,
          this.cabinet.width,
          this.cabinet.depth,
          0,
        ),
      );
    } else if (this.height <= 270) {
      if (this.cabinet.isHanging()) {
        this.drawers.push(
          Drawer.standardDrawer(135, this.cabinet.width, this.cabinet.depth, 0),
        );
        this.drawers.push(
          Drawer.standardDrawer(
            this.height - 135,
            this.cabinet.width,
            this.cabinet.depth,
            1,
          ),
        );
      } else {
        this.drawers.push(
          Drawer.standardDrawer(
            this.height,
            this.cabinet.width,
            this.cabinet.depth,
            0,
          ),
        );
      }
    } else if (this.height <= 370) {
      this.drawers.push(
        Drawer.standardDrawer(200, this.cabinet.width, this.cabinet.depth, 0),
      );
      this.drawers.push(
        Drawer.standardDrawer(
          this.height - 200,
          this.cabinet.width,
          this.cabinet.depth,
          1,
        ),
      );
    } else if (this.height <= 470) {
      if (this.cabinet.isHanging()) {
        this.drawers.push(
          Drawer.standardDrawer(200, this.cabinet.width, this.cabinet.depth, 0),
        );
        this.drawers.push(
          Drawer.standardDrawer(135, this.cabinet.width, this.cabinet.depth, 1),
        );
        this.drawers.push(
          Drawer.standardDrawer(
            this.height - 335,
            this.cabinet.width,
            this.cabinet.depth,
            2,
          ),
        );
      } else {
        this.drawers.push(
          Drawer.standardDrawer(270, this.cabinet.width, this.cabinet.depth, 0),
        );
        this.drawers.push(
          Drawer.standardDrawer(
            this.height - 270,
            this.cabinet.width,
            this.cabinet.depth,
            1,
          ),
        );
      }
    } else if (this.height > 470) {
      this.drawers.push(
        Drawer.standardDrawer(270, this.cabinet.width, this.cabinet.depth, 0),
      );
      this.drawers.push(
        Drawer.standardDrawer(
          this.height - 270,
          this.cabinet.width,
          this.cabinet.depth,
          1,
        ),
      );
    }

    // Propose front DD cross piece type by default, allowing user to add doors in the future
    // Do nothing for shelves under limit for front DD cross pieces
    if (this.cabinet.width > MAX_WIDTH_ACCEPT_FRONT_EP)
      this.rack(0).updateCrossPiece(
        Position.Front,
        undefined,
        undefined,
        CrossPieceType.UpperDoorsLowerDoors,
      );
  }

  updateDrawer(
    position: number,
    height?: number | null,
    width?: number | null,
    depth?: number | null,
    color?: Color | null,
  ) {
    const drawer = this.getDrawer(position);
    if (drawer) {
      const customP = findDrawer(position, drawer, height, width, depth, color);

      if (drawer.handle) {
        // Add the handle(s) to the new customPiece
        customP.handle = drawer.handle;
        if (width) {
          if (!drawer.handles) {
            // Check if this drawer have a slat handle and update it if width change
            // If it's an array, the drawer have handles or pull, otherwise, it's a slat
            customP.handle = findDrawerSlat(width);
          } else {
            // Adjust the number of handles to the width
            if (
              width < MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES &&
              drawer.handles.length > 1
            ) {
              const handle = drawer.handles[0];
              handle.position = Position.Front;
              customP.handle = [handle];
            } else if (
              width >= MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES &&
              drawer.handles.length === 1
            ) {
              const handleLeft = drawer.handles[0];
              handleLeft.position = PositionAngle.FrontLeft;
              const handleRight = cloneDeep(handleLeft);
              handleRight.position = PositionAngle.FrontRight;
              customP.handle = [handleLeft, handleRight];
            }
          }
        }

        if (
          color &&
          (color.reference === ECO_COLOR || color.reference === ECO_PLUS_COLOR)
        ) {
          // Remove the handle
          customP.handle = undefined;
        }
      } else {
        // Eco/Eco+
        if (
          color &&
          color.reference !== ECO_PLUS_COLOR &&
          color.reference !== ECO_COLOR
        ) {
          if (drawer.width < MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES) {
            customP.handle = [DrawerHandle.standardHandle(Position.Front)];
          } else {
            customP.handle = [
              DrawerHandle.standardHandle(PositionAngle.FrontLeft),
              DrawerHandle.standardHandle(PositionAngle.FrontRight),
            ];
          }
        }
      }
      this.drawers = this.updateShelfElements<Drawer>(
        this.drawers,
        customP,
        position,
      );
    }
  }

  addPane(position: Position) {
    for (const rack of this.racks) {
      if (rack.height > 0) {
        if (!rack.getPane(position)) {
          if (position === Position.Left || position === Position.Right) {
            rack.panes.push(
              Pane.standardPane(rack.height, this.cabinet.depth, position),
            );
          } else if (position === Position.Back) {
            rack.panes.push(
              Pane.standardPane(rack.height, this.cabinet.width, position),
            );
          }
        }
      }
    }
    // Cross Pieces will be automatically added or updated by Cross Pieces Manager
  }

  get composition(): ShelfComposition {
    const doorFR = this.getDoor(PositionAngle.FrontRight);
    const doorFL = this.getDoor(PositionAngle.FrontLeft);
    const doorBR = this.getDoor(PositionAngle.BackRight);
    const doorBL = this.getDoor(PositionAngle.BackLeft);

    if (doorBL || doorBR) return ShelfComposition.FourDoors;
    else if (doorFL || doorFR) return ShelfComposition.TwoDoors;

    if (this.drawers.length > 0) return ShelfComposition.Drawer;
    return ShelfComposition.Open;
  }

  set composition(composition: ShelfComposition) {
    switch (composition) {
      case ShelfComposition.Open:
        for (const rack of this.racks) rack.resetRack(this.cabinet.width, true);
        this.removeDoors();
        this.drawers = [];
        // No drawers or door, propose ED or EP cross piece by default
        if (this.cabinet.width > MAX_WIDTH_ACCEPT_FRONT_EP)
          this.rack(0).updateCrossPiece(
            Position.Front,
            undefined,
            undefined,
            CrossPieceType.UpperEmptyLowerDoors,
          );
        else
          this.rack(0).updateCrossPiece(
            Position.Front,
            undefined,
            undefined,
            CrossPieceType.UpperEmptyLowerPane,
            false,
          );
        break;
      case ShelfComposition.TwoDoors:
        this.drawers = [];
        this.removeDoor(PositionAngle.BackLeft);
        this.removeDoor(PositionAngle.BackRight);

        for (const rack of this.racks) {
          rack.resetRack(this.cabinet.width, true);
        }

        this.addDoor(PositionAngle.FrontRight);
        this.addDoor(PositionAngle.FrontLeft);

        this.resetSide();
        this.addPane(Position.Back);
        break;
      case ShelfComposition.FourDoors:
        this.drawers = [];
        for (const rack of this.racks) rack.removePane(Position.Back);

        this.addDoor(PositionAngle.BackRight);
        this.addDoor(PositionAngle.BackLeft);
        this.addDoor(PositionAngle.FrontLeft);
        this.addDoor(PositionAngle.FrontRight);

        this.resetSide();
        // Reinforce if needed
        this.rack(0).updateCrossPiece(
          Position.Back,
          undefined,
          undefined,
          CrossPieceType.UpperDoorsLowerDoors,
          this.width >= MIN_WIDTH_NEED_REINFORCE,
        );
        break;
      case ShelfComposition.Drawer:
        this.setDrawersComposition();
        this.resetSide();
        this.addPane(Position.Back);
        break;
    }
  }

  hasRodRemovedByUser() {
    return this._rodRemovedByUser;
  }

  setDrawersCombination(combo: number[]) {
    /**
     * Change the drawer combos
     */
    let color = findColor(STANDARD_COLOR);
    const oldDrawers = this.drawers;
    if (this.drawers.length > 0) {
      //Get current color if any
      color = this.drawers[0].color;
      // Reinit the drawer for the new combo
      this.drawers = [];
    }

    combo.forEach((drawerHeight, index) => {
      // Get drawer
      const pieces = DataStorage.pieces;
      if (oldDrawers.length > index) {
        color = oldDrawers[index].color;
      }
      const searchInfo = Drawer.getSearchInfos(
        drawerHeight,
        this.cabinet.width,
        this.cabinet.depth,
        color,
      );
      const drawer = pieces.drawers.getCustom(searchInfo, index, true);
      if (drawer) {
        if (
          color.reference !== ECO_COLOR &&
          color.reference !== ECO_PLUS_COLOR
        ) {
          if (oldDrawers.length > index) {
            drawer.handle = oldDrawers[index].handle;
          } else {
            if (drawer.width >= MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES) {
              const hDLeft = DrawerHandle.standardHandle(
                PositionAngle.FrontLeft,
              );
              const hDRight = DrawerHandle.standardHandle(
                PositionAngle.FrontRight,
              );
              drawer.handle = [hDLeft, hDRight];
            } else {
              drawer.handle = [DrawerHandle.standardHandle(Position.Front)];
            }
          }
        }

        this.drawers.push(drawer);
      } else throw new Error('Could not find drawer');
    });
  }

  getDrawersCombo() {
    if (this.drawers.length > 0) {
      return this.drawers.map(drawer => drawer.height);
    }
  }

  addIntermediateShelf(
    height?: number,
    position?: number,
    color?: Color,
    forceLeftColorSupport?: Color,
    forceRightColorSupport?: Color,
  ) {
    /**
     * Add a new intermediate shelf if we have the space for it
     */
    if (position !== undefined) {
      if (this.getIntermediateShelf(position)) {
        throw new Error(
          'An intermediate shelf at the same position already exist',
        );
      }
    } else {
      position = this.intermediatesShelves.length;
    }

    if (this.composition === ShelfComposition.Drawer) return false;

    if (height === undefined) {
      // Compute the height of the new IS and arrange the other IS if needed
      const autoISHeights = findISCombinations(
        this.height,
        this.intermediatesShelves.length + 1,
      );
      if (!autoISHeights) {
        return false;
      }
      height = autoISHeights[autoISHeights.length - 1];
    } else {
      // Check if the height is possible
      let totalHeight = 0;
      for (const entry of this.intermediatesShelves) {
        totalHeight += entry.height;
      }
      if (
        totalHeight + height >
        this.height - MINIMUM_HEIGHT_SPACE_AFTER_ADDING_INTERMEDIATE_SHELF
      ) {
        return false;
      }
    }

    let depth = this.cabinet.depth;
    if (this.intermediatesShelves.length > 0) {
      if (!color) {
        // If no color specify, we reuse the other IS color if identical
        for (const entryIS of this.intermediatesShelves) {
          if (color === undefined) {
            color = entryIS.interShelf.color;
          } else if (entryIS.interShelf.color.reference !== color.reference) {
            color = undefined;
            break;
          }
        }
      }

      // Get the minimal depth of IS bellow
      for (const entryIS of this.intermediatesShelves.slice(0, position)) {
        if (entryIS.interShelf.depth < depth) depth = entryIS.depth;
      }
    }

    // Set glass color as default if one pane or doors are frost
    if (!color && this.racks.length > 0) {
      if (
        this.rack(0)
          .panes.filter(e => e.position !== Position.Back)
          .find(
            e =>
              e.material === PaneMaterial.Frost ||
              e.material === PaneMaterial.Glass,
          ) ||
        this.doors.find(
          e =>
            e.material === PaneMaterial.Frost ||
            e.material === PaneMaterial.Glass,
        )
      ) {
        color = findColor(GLASS_COLOR);
      }
    }

    const firstRack = this.rack(0);
    let colorSupportLeft = undefined;
    let colorSupporRight = undefined;
    if (color?.reference === GLASS_COLOR) {
      colorSupportLeft = findColor(GLASS_COLOR);
      colorSupporRight = findColor(GLASS_COLOR);
    } else {
      if (firstRack) {
        const leftPane = firstRack.getPane(Position.Left);
        const rightPane = firstRack.getPane(Position.Right);
        if (!leftPane || !rightPane) {
          colorSupportLeft = color ?? findColor(WHITE_COLOR);
          colorSupporRight = color ?? findColor(WHITE_COLOR);
        } else {
          if (leftPane.material === PaneMaterial.MDF) {
            colorSupportLeft = leftPane ? leftPane.color : undefined;
          } else {
            colorSupportLeft = color ?? findColor(WHITE_COLOR);
          }

          if (rightPane.material === PaneMaterial.MDF) {
            colorSupporRight = rightPane ? rightPane.color : undefined;
          } else {
            colorSupporRight = color ?? findColor(WHITE_COLOR);
          }
        }
      } else {
        return false;
      }
    }

    const completeIS = CompleteIntermediateShelf.standardCompleteIntermediateShelf(
      position,
      height,
      this.cabinet.width,
      depth,
      this.cabinet.depth,
      forceLeftColorSupport ? forceLeftColorSupport : colorSupportLeft,
      forceRightColorSupport ? forceRightColorSupport : colorSupporRight,
      color,
    );
    this.intermediatesShelves.push(completeIS);
    if (this.intermediatesShelves.length > 1) this.autoArrangeHeightIS();
    return completeIS;
  }

  removeIntermediateShelf(positions: number[], arrange = true) {
    this.intermediatesShelves = this.intermediatesShelves.filter(
      e => !positions.includes(e.position),
    );
    this.intermediatesShelves.sort(
      (a: CompleteIntermediateShelf, b: CompleteIntermediateShelf) =>
        a.position - b.position,
    );
    this.intermediatesShelves.forEach(
      (entry: CompleteIntermediateShelf, key: number) => {
        entry.position = key;
      },
    );
    if (arrange) this.autoArrangeHeightIS();
  }

  autoArrangeHeightIS() {
    /**
     * Adjust the height IS in order to achieve the best combination
     * Remove IS we that can't fit in
     */
    let count = this.intermediatesShelves.length;
    while (count > 0) {
      const autoISHeights = findISCombinations(this.height, count);
      if (autoISHeights) {
        this.intermediatesShelves.forEach((item, index) => {
          item.height = autoISHeights[index];
        });
        return;
      }
      count = count - 1;
      this.removeIntermediateShelf([count], false);
    }
  }

  updateISDepth(value: number, position?: number) {
    /**
     * Update the depth of IS at position
     * Check if upper IS have bigger depth and adjust if needed because a IS should not
     * have depth superior than IS bellow
     */
    if (value > this.cabinet.depth) {
      throw new Error(
        'Could not set the intermediate shelf bigger than cabinet depth',
      );
    }
    if (position !== undefined) {
      const is = this.getIntermediateShelf(position);
      if (!is)
        throw new Error(
          `The intermediate shelf at this position ${position} don't exist`,
        );
      //TODO: if we increase the depth, we should increase the lower IS depth
      is.depth = value;

      if (this.intermediatesShelves.length > position + 1) {
        for (const entryIs of this.intermediatesShelves.slice(position)) {
          if (entryIs.depth > value) {
            entryIs.depth = value;
          }
        }
      }
    } else {
      for (const entryIs of this.intermediatesShelves) {
        entryIs.depth = value;
      }
    }
  }

  setDrillRackCrossPiece(value: boolean) {
    const rack = this.racks.sort((a, b) => a.level - b.level)[0];
    rack.setDrillCrossPiece(value);
  }

  ///////////////////////////////////
  // Start rules functions utility
  ///////////////////////////////////

  // Get number of door handles, includes glass and mirror doors which have some included handle
  numberOfDoorHandles(): number {
    let numberOfHandles = 0;
    for (const door of this.doors) {
      if (
        door.handle ||
        door.material === PaneMaterial.Glass ||
        door.material === PaneMaterial.Mirror
      )
        numberOfHandles++;
    }

    return numberOfHandles;
  }

  changeDoorHolesSize(size: number) {
    for (const door of this.doors) {
      if (!door.handle)
        this.updateDoor(
          door.position as PositionAngle,
          undefined,
          undefined,
          undefined,
          size,
        );
    }
  }

  lastRackHasPaneForPosition(position: Position) {
    return this.rack(this.racks.length - 1).hasPaneForPosition(position);
  }

  firtRackHasPaneForPosition(position: Position) {
    return this.rack(0).hasPaneForPosition(position);
  }

  hasDoorForPosition(position: Position.Front | Position.Back) {
    if (position === Position.Front) return this.hasFrontDoor();
    if (position === Position.Back) return this.hasBackDoor();
  }

  hasFrontDoor(): boolean {
    return (
      !!this.getDoor(PositionAngle.FrontLeft) ||
      !!this.getDoor(PositionAngle.FrontRight)
    );
  }

  hasBackDoor(): boolean {
    return (
      !!this.getDoor(PositionAngle.BackLeft) ||
      !!this.getDoor(PositionAngle.BackRight)
    );
  }

  hasLateralSidePanesInFirstRack() {
    return (
      this.rack(0)?.getPane(Position.Left) &&
      this.rack(0)?.getPane(Position.Right)
    );
  }

  hasDrawers() {
    return this.composition === ShelfComposition.Drawer;
  }

  hasBigDrawers() {
    for (const drawer of this.drawers) {
      if (drawer.height > 270) return true;
    }
    return false;
  }

  isFrontEmpty() {
    return this.composition === ShelfComposition.Open;
  }

  hasReinforcedFrontCrossPiece() {
    const frontCrossPiece = this.rack(0).getCrossPiece(Position.Front);
    if (frontCrossPiece) return frontCrossPiece.base.reinforce;

    return false;
  }

  hasReinforcedBackCrossPiece() {
    const backCrossPiece = this.rack(0).getCrossPiece(Position.Back);
    if (backCrossPiece) return backCrossPiece.base.reinforce;

    return false;
  }

  hasUserReinforcedFrontCrossPiece() {
    return this.rack(0).frontCrossPieceReinforcedByUser;
  }

  setFrontCrossPieces(crossPieceType: CrossPieceType, reinforce?: boolean) {
    const firstRack = this.rack(0);
    if (firstRack) {
      firstRack.updateCrossPiece(
        Position.Front,
        this.cabinet.width,
        undefined,
        crossPieceType,
        reinforce,
      );
    }
  }

  setBackCrossPieces(crossPieceType: CrossPieceType, reinforce?: boolean) {
    const firstRack = this.rack(0);
    if (firstRack) {
      firstRack.updateCrossPiece(
        Position.Back,
        this.cabinet.width,
        undefined,
        crossPieceType,
        reinforce,
      );
    }
  }

  changeReinforceFrontCrossPiece(
    reinforce: boolean,
    reinforcedByUser?: boolean,
  ) {
    const firstRack = this.rack(0);
    if (firstRack) {
      const frontCrossPiece = firstRack.getCrossPiece(Position.Front);
      if (frontCrossPiece) {
        firstRack.updateCrossPiece(
          Position.Front,
          this.cabinet.width,
          undefined,
          undefined,
          reinforce,
        );

        if (typeof reinforcedByUser !== 'undefined')
          firstRack.frontCrossPieceReinforcedByUser = reinforcedByUser;
      }
    }
  }

  changeReinforceBackCrossPiece(reinforce: boolean) {
    const firstRack = this.rack(0);
    if (firstRack) {
      const backCrossPiece = firstRack.getCrossPiece(Position.Back);
      if (backCrossPiece) {
        firstRack.updateCrossPiece(
          Position.Back,
          this.cabinet.width,
          undefined,
          undefined,
          reinforce,
        );
      }
    }
  }

  hasDoorInGlass() {
    for (const door of this.doors) {
      if (door.material === PaneMaterial.Glass) return true;
    }

    return false;
  }

  hasDoorInMirror() {
    for (const door of this.doors) {
      if (door.material === PaneMaterial.Mirror) return true;
    }

    return false;
  }

  hasDrawersOtherThanMDFOrAlu() {
    for (const drawer of this.drawers) {
      if (
        drawer.material !== PaneMaterial.MDF &&
        drawer.material !== PaneMaterial.Alu
      )
        return true;
    }
    return false;
  }

  hasInterShelfOtherThanAllowed() {
    for (const is of this.intermediatesShelves) {
      if (
        is.interShelf.color.reference !== WHITE_COLOR &&
        is.interShelf.color.reference !== BLACK_COLOR &&
        is.interShelf.color.reference !== NATURAL_COLOR &&
        is.interShelf.color.reference !== GLASS_COLOR
      )
        return true;
    }
    return false;
  }

  hasRemovedDoorHandleComparedTo(shelf: Shelf) {
    for (const door of this.doors) {
      const doorToCompare = shelf.getDoor(door.position as PositionAngle);
      if (
        doorToCompare &&
        door.material === doorToCompare.material &&
        door.material !== PaneMaterial.Mirror &&
        door.material !== PaneMaterial.Glass &&
        !door.handle &&
        doorToCompare.handle
      )
        return true;
    }

    return false;
  }

  hasDoorInGlassOrMirrorWithHandle() {
    for (const door of this.doors) {
      if (
        (door.material === PaneMaterial.Mirror ||
          door.material === PaneMaterial.Glass) &&
        door.handle &&
        !door.base.slatMaterial
      )
        return true;
    }

    return false;
  }

  hasDoorInMDFBrut() {
    for (const door of this.doors) {
      if (
        door.material === PaneMaterial.MDF &&
        door.color.reference === NATURAL_COLOR
      )
        return true;
    }

    return false;
  }

  hasFrontDoorInMirror() {
    for (const door of this.doors) {
      if (
        (door.position === PositionAngle.FrontLeft ||
          door.position === PositionAngle.FrontRight) &&
        door.material === PaneMaterial.Mirror
      )
        return true;
    }

    return false;
  }

  hasBackDoorInMirror() {
    for (const door of this.doors) {
      if (
        (door.position === PositionAngle.BackLeft ||
          door.position === PositionAngle.BackRight) &&
        door.material === PaneMaterial.Mirror
      )
        return true;
    }

    return false;
  }

  hasIntermediateShelfDoubleTap() {
    for (const iShelf of this.intermediatesShelves) {
      if (iShelf.interShelf.base.doubleTap) return true;
    }

    return false;
  }

  numberOfIntermediateShelves(minDepth = 0) {
    let counter = 0;
    for (const iShelf of this.intermediatesShelves) {
      if (iShelf.depth >= minDepth) counter++;
    }

    return counter;
  }

  numberOfIntermediateShelvesInGlass() {
    let counter = 0;
    for (const iShelf of this.intermediatesShelves) {
      if (iShelf.interShelf.material === PaneMaterial.Glass) counter++;
    }

    return counter;
  }

  setMaxDepthIntermediateShelves(maxDepth: number) {
    for (const iShelf of this.intermediatesShelves) {
      if (iShelf.depth > maxDepth) iShelf.depth = maxDepth;
    }
  }

  hasDoorInAluOrFrost() {
    for (const door of this.doors) {
      if (
        door.material === PaneMaterial.Alu ||
        door.material === PaneMaterial.Frost ||
        door.material === PaneMaterial.FrostOpaque
      )
        return true;
    }

    return false;
  }

  hasBackpaneIn(material: PaneMaterial) {
    for (const rack of this.racks) {
      if (rack.getPane(Position.Back)?.material === material) return true;
    }

    return false;
  }

  hasSidepaneIn(material: PaneMaterial) {
    for (const rack of this.racks) {
      if (
        rack.getPane(Position.Left)?.material === material ||
        rack.getPane(Position.Right)?.material === material
      )
        return true;
    }

    return false;
  }

  hasBigDoorHandleInAluminium() {
    for (const door of this.doors) {
      if (door.base.slatMaterial === Material.Aluminium) return true;
    }

    return false;
  }

  hasBigDoorHandleInWood() {
    for (const door of this.doors) {
      if (door.base.slatMaterial === Material.Wood) return true;
    }

    return false;
  }

  setBigDoorsHandlesToAluminium() {
    for (const door of this.doors) {
      // @ts-ignore
      this.setLargeDoorSlat(door.position, Material.Aluminium);
    }
  }

  setBigDoorsHandlesToWood() {
    for (const door of this.doors) {
      // @ts-ignore
      this.setLargeDoorSlat(door.position, Material.Wood);
    }
  }

  // End rules

  addDoorHandle(position: PositionAngle, type: DoorHandleType, color?: Color) {
    /**
     * set a door handle to the door at position in param
     * If no door, do nothing
     * If there is already a handle to the door, update it
     */
    let door = this.getDoor(position);
    if (
      !door ||
      this.isBigShelf() ||
      this.composition === ShelfComposition.Drawer
    )
      return;

    let colorHandle = color;
    let hole = undefined;
    if (type === DoorHandleType.Button || type === DoorHandleType.BigButton) {
      colorHandle = findColor(ALUMINIUM_COLOR);
      hole = DoorHole.button;
    } else if (!color) {
      colorHandle = findColor(WHITE_COLOR);
    }

    if (type === DoorHandleType.Knop) {
      hole = DoorHole.cupLarge;
    }

    const doorHandle = findDoorHandle(type, colorHandle);
    if (doorHandle) {
      if (hole && hole !== door.base.hole) {
        this.updateDoor(
          door.position as PositionAngle,
          undefined,
          undefined,
          undefined,
          hole,
        );
        door = this.getDoor(position);
        if (!door) throw new Error('Should have a door');
      }
      door.handle = doorHandle;
    } else {
      throw new Error(
        "Could not add handle to the door, the handle don't exist",
      );
    }
  }

  prepareHandle() {
    /**
     * Ensure there is always a default handle for the door/drawers of the shelf
     */
    const colors = getColors();
    const colorWhite = colors.find(e => e.reference === WHITE_COLOR);
    if (this.composition === ShelfComposition.Drawer) {
      for (const drawer of this.drawers) {
        if (!drawer.getDrawerHandle(PositionAngle.FrontLeft)) {
          this.addDrawerHandle(
            drawer.position as number,
            DrawerHandleType.Knop,
            colorWhite,
          );
        }
        if (!drawer.getDrawerHandle(PositionAngle.FrontRight)) {
          this.addDrawerHandle(
            drawer.position as number,
            DrawerHandleType.Knop,
            colorWhite,
          );
        }
      }
    } else if (!this.isBigShelf()) {
      for (const position of Object.values(PositionAngle)) {
        const door = this.getDoor(position);
        if (door) {
          if (!door.getDrawerHandle(position))
            this.addDoorHandle(position, DoorHandleType.Knop, colorWhite);
        }
      }
    }
  }

  removeDoorHandle(position?: PositionAngle) {
    /**
     * If no positions, remove all the handle of all doors
     */
    if (position) {
      const door = this.getDoor(position);
      if (door) {
        door.handle = undefined;
      }
      this.updateDoor(
        position,
        undefined,
        undefined,
        undefined,
        DoorHole.cupLarge,
      );
    } else {
      for (const door of this.doors) {
        door.handle = undefined;
        this.updateDoor(
          door.position as PositionAngle,
          undefined,
          undefined,
          undefined,
          DoorHole.cupLarge,
        );
      }
    }
  }

  setDrawerHandle(
    position: number,
    handle: CustomPiece<DrawerHandle>[] | CustomPiece<DrawerSlat>,
  ) {
    /**
     * Same as setDoorHandle
     */
    const drawer = this.getDrawer(position);
    if (drawer) {
      drawer.handle = handle;
    }
  }

  isBigShelf() {
    return this.height > MINIMUM_LARGE_DOORS_HEIGHT;
  }

  setLargeDoorSlat(position: PositionAngle, material: Material) {
    if (this.isBigShelf())
      this.updateDoor(position, null, null, null, null, null, material);
  }

  addDrawerHandle(
    positionDrawer: number,
    type: DrawerHandleType,
    color?: Color,
    defaultDrawerColor?: Color,
  ) {
    /**
     * defaultDrawerColor is use when we choose a pull/button for drawer from eco/eco+
     */
    if (this.drawers.length === 0 || !this.getDrawer(positionDrawer)) return;
    if (type !== DrawerHandleType.Slat) {
      if (!color) {
        color = findColor(WHITE_COLOR);
        if (!color)
          throw new Error(
            'White color should exist, colors should be loaded before calling the function',
          );
      }

      const drawer = this.getDrawer(positionDrawer);
      if (!drawer) {
        throw new Error(
          `The drawer at the position ${positionDrawer} don't exist in the shelf`,
        );
      }

      let drawerHandleSides: PositionPiece[] = [];
      if (this.width >= BIG_SHELF_MINIMUM_HEIGHT) {
        drawerHandleSides = [PositionAngle.FrontLeft, PositionAngle.FrontRight];
        drawer.handle = drawer.handles?.filter(
          e => e.position !== Position.Front,
        );
      } else {
        drawerHandleSides = [Position.Front];
        drawer.handle = drawer.handles?.filter(
          e => e.position === Position.Front,
        );
      }

      drawerHandleSides.map(positionOnDrawer => {
        const drawerHandle = findDrawerHandle(
          positionOnDrawer,
          type,
          color as Color,
        );

        if (!drawer.handles) drawer.handle = [];
        // @ts-ignore
        if (drawer.handles.length > 0) {
          // @ts-ignore
          drawer.handle = drawer.handles.filter(
            e => e.position !== positionOnDrawer,
          );
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        drawer.handle.push(drawerHandle);
      });
    } else {
      const drawerSlat = findDrawerSlat(this.width);
      const drawer = this.getDrawer(positionDrawer);
      if (drawer) {
        drawer.handle = drawerSlat;
        drawer.handle.position = Position.Front;
      }
    }
  }

  updateSupportIntermediateShelf(
    position: Position.Left | Position.Right,
    color: Color,
  ) {
    if (this.intermediatesShelves) {
      for (const iS of this.intermediatesShelves) {
        iS.updateColorSupport(position, color);
      }
    }
  }

  resetSide() {
    this.addPane(Position.Left);
    this.addPane(Position.Right);
  }

  addRemainingCrossPieces() {
    Object.values(Position).forEach(position => {
      const cp = this.rack(0).getCrossPiece(position);
      if (!cp) {
        const type = getDefaultCrossPieceType(
          acceptedCrossPieceTypesForShelf(this, this.rack(0), position),
        );
        this.rack(0).addCrossPiece(
          position,
          position === Position.Left || position === Position.Right
            ? this.cabinet.depth
            : this.cabinet.width,
          undefined,
          type,
          this.cabinet.width >= MIN_WIDTH_NEED_REINFORCE &&
            position === Position.Front,
        );
      }
    });

    checkAndUpdate(this.cabinet);
  }

  updateSupportIS() {
    if (this.intermediatesShelves.length > 0) {
      const colorSupportRight = this.rack(0).getPane(Position.Right)?.color;
      const colorSupportLeft = this.rack(0).getPane(Position.Left)?.color;
      for (const entryIS of this.intermediatesShelves) {
        const colorIS = entryIS.interShelf.color;
        if (colorIS.reference !== GLASS_COLOR) {
          if (colorSupportRight) {
            if (getMaterialColor(colorSupportRight) === PaneMaterial.MDF) {
              entryIS.updateSupportIntermediateShelf(
                Position.Right,
                undefined,
                undefined,
                colorSupportRight,
              );
            } else if (
              [WHITE_COLOR, BLACK_COLOR, NATURAL_COLOR, GLASS_COLOR].includes(
                entryIS.interShelf.color.reference,
              )
            ) {
              entryIS.updateSupportIntermediateShelf(
                Position.Right,
                undefined,
                undefined,
                entryIS.interShelf.color,
              );
            } else {
              entryIS.updateSupportIntermediateShelf(
                Position.Right,
                undefined,
                undefined,
                findColor(WHITE_COLOR),
              );
            }
          }

          if (colorSupportLeft) {
            if (getMaterialColor(colorSupportLeft) === PaneMaterial.MDF) {
              entryIS.updateSupportIntermediateShelf(
                Position.Left,
                undefined,
                undefined,
                colorSupportLeft,
              );
            } else if (
              [WHITE_COLOR, BLACK_COLOR, NATURAL_COLOR, GLASS_COLOR].includes(
                entryIS.interShelf.color.reference,
              )
            ) {
              entryIS.updateSupportIntermediateShelf(
                Position.Left,
                undefined,
                undefined,
                entryIS.interShelf.color,
              );
            } else {
              entryIS.updateSupportIntermediateShelf(
                Position.Left,
                undefined,
                undefined,
                findColor(WHITE_COLOR),
              );
            }
          }
        }
      }
    }
  }
}
